import { graphql, useStaticQuery, Link } from "gatsby"
import { faArrowRight } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import React from "react"
import tw from "twin.macro"
import { CustomerSuccessStoryCardNewStoryblok, CustomerSuccessStoryStoryblok } from "../../../component-types-sb"
import { getImageDimensions } from "../../utils/getImageDimensions"
import Image from "../general/Image"
import { H4 } from "../typography/Typography"
import { parseUseCase } from "../general/CustomerSuccessStoriesGrid"
import useLocale from "../../hooks/useLocale"
import CardBase from "./CardBase"

interface CardProps {
  readonly blok: CustomerSuccessStoryCardNewStoryblok
}

const ImageContainer = tw.div`
  relative
  w-full
  flex
  items-center
  overflow-hidden
  gap-y-4
  grid
  p-4 pb-0
`

const TextContainer = tw.div`
  p-4 pt-0 flex flex-col h-full
`

const CustomerSuccessStoryCardNew: React.FC<CardProps> = ({ blok }) => {
  // Query to resolve the success_story field
  const data = useStaticQuery(graphql`
    query CustomerSuccessStoryQuery {
      allStoryblokEntry(filter: { field_component: { eq: "customerSuccessStory" } }) {
        nodes {
          uuid
          content
          full_slug
          lang
        }
      }
    }
  `)

  const { locale } = useLocale()

  // Find the success_story data by matching the UUID
  const successStoryData = data.allStoryblokEntry.nodes.find(
    (node: { uuid: string; lang: string }) =>
      node.uuid === blok.success_story && node.lang === locale
  )

  const successStorySlug = successStoryData.full_slug

  if (!successStorySlug) {
    console.error("CustomerSuccessStoryCardNew: full_slug is missing for success_story", successStoryData)
    return null
  }

  const successStory: CustomerSuccessStoryStoryblok | null = successStoryData
    ? JSON.parse(successStoryData.content)
    : null

  if (!successStory) {
    console.error("CustomerSuccessStoryCardNew: success_story is not resolved or invalid", blok.success_story)
    return null
  }

  const imageWidth = successStory.image ? getImageDimensions(successStory.image).width : 0

  // Parse the use_case field
  const parsedUseCase = parseUseCase(successStory.use_case)
  const useCaseTitle = parsedUseCase?.content?.title

  return (
    <Link to={`/${successStorySlug}`} className="group">
      <CardBase tw="relative flex flex-col h-fit rounded-2xl bg-white-blue-2">
        <ImageContainer>
          {successStory.image && (
            <Image
              image={successStory.image}
              fit="contain"
              height="100%"
              width="100%"
              fluid={imageWidth}
              tw="rounded-2xl"
            />
          )}
          {useCaseTitle && (
            <div className="tag" tw="w-fit p-1.5 px-3">
              {useCaseTitle}
            </div>
          )}
        </ImageContainer>

        <TextContainer>
          <H4 tw="pt-0">{successStory.title}</H4>
          <div tw="text-17 self-end mt-auto">
            <FontAwesomeIcon tw="group-hocus:(animate-bounce-x)" icon={faArrowRight} />
          </div>
        </TextContainer>
      </CardBase>
    </Link>
  )
}

export default CustomerSuccessStoryCardNew
